/**
 * WHAT A REPORT READ SAYS ABOUT ITSELF (M8; the build plan's 4.7 and 6.11).
 *
 * Under the new permission model a report says which of its figures the reader
 * may see in at least one piece of their access, and, on a grouped row, which
 * figures in that row leave out values the reader may not see. The API puts the
 * first on the response's headers (ReportAnswer.cs) and the second in the row's
 * `partial`, both as the figures' JSON names.
 *
 * Under the new model a blank figure means WITHHELD, and only that: a figure the
 * reader may see over no rows is 0. So a page stops guessing from nulls and
 * says "withheld". Under today's model the API says nothing, `vis` is null, and
 * every page draws exactly what it drew before.
 */

/** The new model's account of one read. */
export interface Visibility {
  /** The figures some piece of the reader's access shows, by the names the report gives them. */
  shown: ReadonlySet<string>
  /**
   * The sales figures are the reader's team's, through the hierarchy, not all of
   * the item's sales (Material Planning: "Sales: your team"). Null where the
   * report does not say.
   */
  teamSales: boolean | null
}

/** A report read: its body as before, and what the new model says about it (null under today's). */
export interface Answer<T> {
  data: T
  vis: Visibility | null
}

/** Reads the API's headers; null when the response is not the new model's. */
export function visibilityOf(headers: Headers): Visibility | null {
  if (headers.get('X-MBI-Model') !== 'v2') return null
  const team = headers.get('X-MBI-Team-Sales')
  return {
    shown: new Set((headers.get('X-MBI-Shown') ?? '').split(',').map((s) => s.trim()).filter(Boolean)),
    teamSales: team === null ? null : team === '1',
  }
}

/** The figures of one row that leave hidden values out. Empty under today's model. */
export const partialOf = (row: { partial?: string | null } | null | undefined): ReadonlySet<string> =>
  new Set((row?.partial ?? '').split(',').map((s) => s.trim()).filter(Boolean))

/** The words, one copy: every page says the same thing about the same state. */
export const WITHHELD = 'withheld'
export const WITHHELD_TITLE = 'Withheld: your access does not show you this figure here'
export const PARTIAL_TITLE = 'Partly withheld: this leaves out what your access does not show you'
/** An extract's note when some figure in it leaves withheld values out: a file carries no mark per cell. */
export const PARTIAL_NOTE = 'Some figures here leave out values your access does not show you; on screen they are marked *.'

/** A total of figures over rows, as the new model draws it. */
export interface ShownTotal {
  /** The sum of what shows; null when every row withholds every part. */
  value: number | null
  /** Some row withholds a part, or leaves hidden values out of it. */
  partial: boolean
}

/**
 * THE NEW MODEL'S TOTAL (M8; the build plan's R4-2). A figure can be withheld on
 * some rows and shown on others, one piece of access at a time, so a total is the
 * sum of what shows, marked when it leaves anything out, and withheld only when
 * nothing shows. Each part names the figure as Partial does, and how to read it.
 */
export function sumShown<R extends { partial?: string | null }>(
  rows: readonly R[],
  ...parts: [field: string, pick: (row: R) => number | null][]
): ShownTotal {
  let value: number | null = null
  let leftOut = false
  for (const r of rows) {
    const p = partialOf(r)
    for (const [field, pick] of parts) {
      const v = pick(r)
      if (v === null) leftOut = true
      else value = (value ?? 0) + v
      if (p.has(field)) leftOut = true
    }
  }
  return { value, partial: value !== null && leftOut }
}
